import React, { useState } from 'react';
import { CreatePatientData } from '../services/patientsService';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../hooks/useAuth';

interface CreatePatientModalProps {
  open: boolean;
  onSave: (data: CreatePatientData) => Promise<void>;
  onCancel: () => void;
}

const CreatePatientModal: React.FC<CreatePatientModalProps> = ({ 
  open, 
  onSave, 
  onCancel 
}) => {
  const [formData, setFormData] = useState<CreatePatientData>({
    email: '',
    firstName: '',
    lastName: '',
    gender: 'male',
    dateOfBirth: '',
    rmrKcalDay: 1800,
    weight: 70,
    phoneNumber: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreatePatientData, string>>>({});
  const isMobile = useIsMobile();
  const { user } = useAuth();
  // Same visibility rule as SendTemplateEmailModal's monarkaOnly templates
  const canSendMonarkaEmails =
    user?.role === 'admin' ||
    (user?.role === 'doctor' && user?.email?.toLowerCase().endsWith('@monarkaclinic.com'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        gender: 'male',
        dateOfBirth: '',
        rmrKcalDay: 1800,
        weight: 70,
        phoneNumber: ''
      });
      setErrors({});
      setSubmitError('');
    }
  }, [open]);

  const handleFieldChange = (field: keyof CreatePatientData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setSubmitError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreatePatientData, string>> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'La fecha de nacimiento es requerida';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 0 || age > 120) {
        newErrors.dateOfBirth = 'Fecha de nacimiento inválida';
      }
    }

    // RMR validation
    if (formData.rmrKcalDay <= 0) {
      newErrors.rmrKcalDay = 'El RMR debe ser mayor a 0';
    }

    // Weight validation
    if (formData.weight <= 0) {
      newErrors.weight = 'El peso debe ser mayor a 0';
    }

    // Phone number validation (optional field)
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const phoneNumber = formData.phoneNumber.trim();
      
      // Check max length (255 characters)
      if (phoneNumber.length > 255) {
        newErrors.phoneNumber = 'El número de teléfono no puede exceder 255 caracteres';
      } 
      // Optional: Basic format validation for international phone numbers
      // Accepts formats like: +1234567890, +54 338 5400000, +1-234-567-8900, etc.
      else if (!/^[\d\s\-+()]+$/.test(phoneNumber)) {
        newErrors.phoneNumber = 'El número de teléfono solo puede contener dígitos, espacios, guiones, paréntesis y el símbolo +';
      }
      // Check if it has at least some digits
      else if (!/\d/.test(phoneNumber)) {
        newErrors.phoneNumber = 'El número de teléfono debe contener al menos un dígito';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await onSave(formData);
    } catch (error: any) {
      console.error('Error creating patient:', error);
      
      // Handle field-specific errors from backend (400 errors)
      if (error.response && typeof error.response === 'object') {
        const backendErrors: Partial<Record<keyof CreatePatientData, string>> = {};
        let hasFieldErrors = false;
        
        // Process each field error from backend
        Object.keys(error.response).forEach((field) => {
          const errorMessages = error.response[field];
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            hasFieldErrors = true;
            // Map backend field names to frontend field names
            if (field === 'phone_number') {
              backendErrors.phoneNumber = errorMessages[0];
            } else if (field in formData) {
              backendErrors[field as keyof CreatePatientData] = errorMessages[0];
            }
          }
        });
        
        // If we have field-specific errors, show them
        if (hasFieldErrors) {
          setErrors(backendErrors);
          setSubmitError('Por favor corrige los errores en el formulario.');
        } else {
          // Generic error message
          setSubmitError(error.message || 'Error al crear el paciente. Por favor intente nuevamente.');
        }
      } else {
        setSubmitError(error.message || 'Error al crear el paciente. Por favor intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.18)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000,
      padding: isMobile ? '12px' : '20px'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: isMobile ? '24px 20px' : '32px 40px',
        width: '100%',
        maxWidth: 560,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)' 
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          Crear Nuevo Paciente
        </h2>

        {submitError && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 14
          }}>
            {submitError}
          </div>
        )}
        
        {/* Email */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Email <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input 
            type="email"
            value={formData.email} 
            onChange={e => handleFieldChange('email', e.target.value)} 
            autoComplete="off"
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.email ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }} 
            placeholder="ejemplo@correo.com"
          />
          {errors.email && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.email}
            </span>
          )}
        </label>

        {/* First Name */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Nombre <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input 
            value={formData.firstName} 
            onChange={e => handleFieldChange('firstName', e.target.value)} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.firstName ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }} 
            placeholder="Juan"
          />
          {errors.firstName && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.firstName}
            </span>
          )}
        </label>

        {/* Last Name */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Apellido <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input 
            value={formData.lastName} 
            onChange={e => handleFieldChange('lastName', e.target.value)} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.lastName ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }} 
            placeholder="Pérez"
          />
          {errors.lastName && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.lastName}
            </span>
          )}
        </label>

        {/* Phone Number */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Número de Teléfono
          </span>
          <input 
            type="tel"
            value={formData.phoneNumber || ''} 
            onChange={e => handleFieldChange('phoneNumber', e.target.value)} 
            onPaste={e => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const sanitized = pastedText.replace(/[^\d+]/g, '');
              handleFieldChange('phoneNumber', sanitized);
            }}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.phoneNumber ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }} 
            placeholder="+34666112233"
            maxLength={255}
          />
          {errors.phoneNumber && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.phoneNumber}
            </span>
          )}
          <span style={{ color: '#6b7280', fontSize: 13, marginTop: 6, display: 'block', lineHeight: 1.4 }}>
            Formato internacional recomendado: +[código país][número]
          </span>
        </label>

        {/* Gender */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Género <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <select 
            value={formData.gender} 
            onChange={e => handleFieldChange('gender', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              background: '#fff',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }}
          >
            <option value="male">Masculino</option>
            <option value="female">Femenino</option>
            <option value="other">Otro</option>
          </select>
        </label>

        {/* Date of Birth */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Fecha de Nacimiento <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input 
            type="date"
            value={formData.dateOfBirth} 
            onChange={e => handleFieldChange('dateOfBirth', e.target.value)} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.dateOfBirth ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }} 
          />
          {errors.dateOfBirth && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.dateOfBirth}
            </span>
          )}
        </label>

        {/* RMR */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            RMR (kcal/día) <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input 
            type="number" 
            value={formData.rmrKcalDay} 
            onChange={e => handleFieldChange('rmrKcalDay', parseFloat(e.target.value) || 0)} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.rmrKcalDay ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box'
            }} 
            min="0"
            step="1"
          />
          {errors.rmrKcalDay && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.rmrKcalDay}
            </span>
          )}
        </label>

        {/* Weight */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Peso (kg) <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input 
            type="number" 
            value={formData.weight} 
            onChange={e => handleFieldChange('weight', parseFloat(e.target.value) || 0)} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: `1px solid ${errors.weight ? '#dc2626' : '#cbd5e1'}`, 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box'
            }} 
            min="0"
            step="0.1"
          />
          {errors.weight && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.weight}
            </span>
          )}
        </label>

        {/* Welcome Email Type */}
        <label style={{ display: 'block', marginBottom: 28 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Tipo de Email de Bienvenida
          </span>
          <select 
            value={formData.welcomeEmailType || ''} 
            onChange={e => handleFieldChange('welcomeEmailType', e.target.value || undefined)}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              background: '#fff',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }}
          >
            <option value="">Sin Email de Bienvenida</option>
            <option value="welcome">Bienvenida</option>
            <option value="welcomeWithPassword">Bienvenida con Contraseña</option>
            {canSendMonarkaEmails && (
              <>
                <option value="welcomeMonarka">Bienvenida Monarka</option>
                <option value="welcomeMonarkaWithPassword">Bienvenida Monarka con Contraseña</option>
              </>
            )}
          </select>
          <span style={{ color: '#6b7280', fontSize: 13, marginTop: 6, display: 'block', lineHeight: 1.4 }}>
            Selecciona si deseas enviar un email de bienvenida al paciente
          </span>
        </label>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 8, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button 
            onClick={onCancel} 
            disabled={isSubmitting}
            style={{ 
              background: '#f3f4f6', 
              border: 'none', 
              borderRadius: 6, 
              padding: '11px 24px', 
              fontWeight: 600, 
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: 15,
              opacity: isSubmitting ? 0.6 : 1,
              color: '#374151',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.currentTarget.style.background = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
          >
            Cancelar
          </button>
          <button 
            onClick={handleSave} 
            disabled={isSubmitting}
            style={{ 
              background: isSubmitting ? '#81c9c4' : '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '11px 24px', 
              fontWeight: 600, 
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: 15,
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.currentTarget.style.background = '#319795';
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.currentTarget.style.background = '#38b2ac';
            }}
          >
            {isSubmitting ? 'Creando...' : 'Crear Paciente'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePatientModal;

